/**
 * ClimbMapView Component
 *
 * Displays the gym floor plan with interactive route pins.
 * Wraps the base MapStage with route pins overlay.
 *
 * Features:
 * - Floor plan image as background
 * - Route pins with grades and completion status
 * - Pan and zoom support
 * - Route selection on pin tap
 *
 * @example
 * ```tsx
 * <ClimbMapView
 *   floorPlan={gym.floorPlan}
 *   routes={routes}
 *   colorGrades={gym.colorGrades}
 *   sentRouteIds={sentRouteIds}
 *   containerWidth={800}
 *   containerHeight={600}
 *   onRouteSelect={(route) => openRouteSheet(route)}
 * />
 * ```
 */

import { useMemo, useState, useEffect, useRef, type CSSProperties } from 'react';
import { Image as KonvaImage } from 'react-konva';
import { MapStage, type MapStageRef } from '../gym-map/MapStage';
import { RoutePinsLayer } from '../gym-map/RoutePinsLayer';
import { RegionsLayer } from '../gym-map/RegionsLayer';
import { RegionIndicator } from './RegionIndicator';
import { useFloorPlanImage } from '@/hooks/useFloorPlanImage';
import { useMapDrillDown } from '@/hooks/useMapDrillDown';
import type { GymFloorPlan, Route, ColorGrade, PrimaryIdentificationMethod } from '@flash/shared';

/**
 * Props for ClimbMapView component
 */
export interface ClimbMapViewProps {
  /** Floor plan configuration with image and regions */
  floorPlan: GymFloorPlan;
  /** Routes to display as pins */
  routes: Route[];
  /** Gym's color grades for hold colors (or all colors when linked) */
  colorGrades: ColorGrade[];
  /** Gym's tag color grades (only used when colors are unlinked) */
  tagColorGrades?: ColorGrade[] | undefined;
  /** Primary identification method from gym settings */
  primaryMethod?: PrimaryIdentificationMethod | undefined;
  /** Set of route IDs the user has sent */
  sentRouteIds: Set<string>;
  /** Width of the container in pixels */
  containerWidth: number;
  /** Height of the container in pixels */
  containerHeight: number;
  /** Currently selected route ID (optional) */
  selectedRouteId?: string;
  /** Called when a route is selected */
  onRouteSelect?: (route: Route) => void;
  /** Additional CSS class name for the container */
  className?: string;
  /** Whether to animate the view on mount (default: false) */
  animate?: boolean;
  /** Pin size in screen pixels at base scale (default: 32) */
  pinSize?: number;
}

/**
 * Calculate the scale to fit the image within the container while maintaining aspect ratio
 */
const calculateFitScale = (
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number
): number => {
  const scaleX = containerWidth / imageWidth;
  const scaleY = containerHeight / imageHeight;
  return Math.min(scaleX, scaleY);
};

/**
 * ClimbMapView Component
 *
 * Displays the gym floor plan with route pins for the climb tab.
 */
export const ClimbMapView = ({
  floorPlan,
  routes,
  colorGrades,
  tagColorGrades,
  primaryMethod = 'holdColors',
  sentRouteIds,
  containerWidth,
  containerHeight,
  selectedRouteId,
  onRouteSelect,
  className,
  animate = false,
  pinSize = 32,
}: ClimbMapViewProps) => {
  // Ref for imperative map control
  const mapStageRef = useRef<MapStageRef>(null);

  // Animation state for view transitions
  const [isVisible, setIsVisible] = useState(!animate);

  // Trigger animation on mount
  useEffect(() => {
    if (animate) {
      const raf = requestAnimationFrame(() => {
        setIsVisible(true);
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [animate]);

  // Load the floor plan image
  const { image, isLoading, hasError, naturalWidth, naturalHeight } = useFloorPlanImage(
    floorPlan.imageUrl
  );

  // Use stored dimensions if available, otherwise use loaded dimensions
  const imageWidth = floorPlan.imageWidth || naturalWidth;
  const imageHeight = floorPlan.imageHeight || naturalHeight;

  // Calculate initial scale to fit image in container
  const initialScale = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0) return 1;
    return calculateFitScale(imageWidth, imageHeight, containerWidth, containerHeight);
  }, [imageWidth, imageHeight, containerWidth, containerHeight]);

  // Calculate initial position to center the image
  const initialPosition = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0) return { x: 0, y: 0 };

    const scaledWidth = imageWidth * initialScale;
    const scaledHeight = imageHeight * initialScale;

    return {
      x: (containerWidth - scaledWidth) / 2,
      y: (containerHeight - scaledHeight) / 2,
    };
  }, [imageWidth, imageHeight, containerWidth, containerHeight, initialScale]);

  // Check if gym has regions defined
  const hasRegions = floorPlan.regions && floorPlan.regions.length > 0;

  // Drill-down state management
  const drillDown = useMapDrillDown({
    regions: floorPlan.regions || [],
    initialScale,
    imageWidth,
    imageHeight,
    containerWidth,
    containerHeight,
    mapStageRef,
  });

  // Keep a ref to zoomToPoint to avoid dependency issues
  const zoomToPointRef = useRef(drillDown.zoomToPoint);
  zoomToPointRef.current = drillDown.zoomToPoint;

  // Auto-center on route selection - runs immediately when selectedRouteId changes
  useEffect(() => {
    if (selectedRouteId) {
      const selectedRoute = routes.find((r) => r.id === selectedRouteId);
      if (selectedRoute?.mapPosition) {
        // Center the selected route in the visible area above the route card
        // Route card takes ~40vh, so offset by ~10% to position pin in upper portion
        // Use ref to get latest zoomToPoint without it being a dependency
        zoomToPointRef.current(selectedRoute.mapPosition, { offsetY: 0.1 });
      }
    }
  }, [selectedRouteId, routes]);

  // Compute animation styles
  const animationStyle: CSSProperties = animate
    ? {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.95)',
        transition: 'opacity 300ms ease-out, transform 300ms ease-out',
      }
    : {};

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          ...animationStyle,
          width: containerWidth,
          height: containerHeight,
        }}
        className={className}
      >
        <div style={loadingStyle}>
          <div style={spinnerStyle} />
          <p style={loadingTextStyle}>Loading map...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div
        style={{
          ...containerStyle,
          ...animationStyle,
          width: containerWidth,
          height: containerHeight,
        }}
        className={className}
      >
        <div style={errorStyle}>
          <p style={errorTextStyle}>Failed to load floor plan</p>
        </div>
      </div>
    );
  }

  // Build MapStage props conditionally
  const mapStageProps = {
    width: containerWidth,
    height: containerHeight,
    imageWidth,
    imageHeight,
    initialViewport: {
      x: initialPosition.x,
      y: initialPosition.y,
      scale: initialScale,
    },
    minScale: initialScale * 0.5,
    maxScale: 4,
    onViewportChange: drillDown.handleViewportChange,
    ...(className ? { className } : {}),
  };

  // Build RoutePinsLayer props conditionally
  // Show pins when zoomed in (pins mode) or when no regions are defined
  const showPins = drillDown.viewMode === 'pins' || !hasRegions;
  const routePinsProps = {
    routes,
    sentRouteIds,
    colorGrades,
    tagColorGrades,
    primaryMethod,
    imageWidth,
    imageHeight,
    pinSize,
    visible: showPins,
    viewportScale: drillDown.viewport.scale,
    initialScale,
    ...(selectedRouteId ? { selectedRouteId } : {}),
    ...(onRouteSelect ? { onRouteClick: onRouteSelect } : {}),
  };

  return (
    <div
      style={{
        ...wrapperStyle,
        ...animationStyle,
        width: containerWidth,
        height: containerHeight,
        position: 'relative',
      }}
    >
      {/* Region indicator overlay - shows current region when zoomed in */}
      {hasRegions && (
        <RegionIndicator
          regionName={drillDown.currentRegion}
          visible={drillDown.viewMode === 'pins'}
        />
      )}

      <MapStage ref={mapStageRef} {...mapStageProps}>
        {/* Floor plan background image */}
        {image && (
          <KonvaImage image={image} width={imageWidth} height={imageHeight} listening={false} />
        )}

        {/* Regions layer - visible when zoomed out */}
        {hasRegions && (
          <RegionsLayer
            regions={floorPlan.regions}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            visible={drillDown.viewMode === 'regions'}
            onRegionClick={drillDown.zoomToRegion}
          />
        )}

        {/* Route pins layer - visible when zoomed in or no regions defined */}
        <RoutePinsLayer {...routePinsProps} />
      </MapStage>
    </div>
  );
};

// Styles
const wrapperStyle: CSSProperties = {
  borderRadius: '8px',
  overflow: 'hidden',
};

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  borderRadius: '8px',
  overflow: 'hidden',
};

const loadingStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
};

const spinnerStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  border: '3px solid var(--color-border, #e5e7eb)',
  borderTopColor: 'var(--color-primary, #3b82f6)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const errorStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-error, #ef4444)',
};

export default ClimbMapView;
